import React, { useEffect, useState } from "react";
import { AlertTriangle, MapPin, X, ChevronRight } from "lucide-react";
import { useNavigate } from "react-router-dom";

/**
 * Slide-down emergency banner that appears at the top of the citizen
 * shell whenever a fresh broadcast arrives via WebSocket
 * (`useCitizenBroadcasts` in App.js). Distinct from the existing
 * polling-based <BroadcastBanner /> which renders the FULL list of
 * active broadcasts further down the home page; this component handles
 * the *transient* push notification a citizen sees the moment an admin
 * publishes a new alert.
 *
 * Behaviour:
 *   • Slides in from above with a 300 ms ease-out translate-Y animation.
 *   • Severity tints — critical = red, warning = amber, info = cyan.
 *   • Title + category + relative-time + first 120 chars of message.
 *   • Tap anywhere → navigate to `/broadcasts/:id`.
 *   • Close button stops the auto-dismiss timer immediately.
 *   • role="alert" so screen readers announce the new broadcast.
 */

const SEVERITY = {
  critical: { bg: "bg-rose-600", ring: "ring-rose-300/40", label: "CRITIQUE" },
  warning:  { bg: "bg-amber-500", ring: "ring-amber-300/40", label: "AVERTISSEMENT" },
  info:     { bg: "bg-cyan-600",  ring: "ring-cyan-300/40", label: "INFORMATION" },
};

const ago = (iso) => {
  if (!iso) return "à l'instant";
  const s = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.floor(s / 60)} min`;
  return `${Math.floor(s / 3600)} h`;
};

export const LiveBroadcastBanner = ({ broadcast, onDismiss }) => {
  const navigate = useNavigate();
  const [enter, setEnter] = useState(false);

  useEffect(() => {
    if (broadcast) {
      const id = requestAnimationFrame(() => setEnter(true));
      return () => cancelAnimationFrame(id);
    }
    Promise.resolve().then(() => setEnter(false));
    return undefined;
  }, [broadcast]);

  if (!broadcast) return null;
  const sev = SEVERITY[broadcast.severity] || SEVERITY.info;
  const preview = (broadcast.message || "").slice(0, 120);

  const open = () => {
    try { navigate(`/broadcasts/${broadcast.id}`); } catch { /* noop */ }
    onDismiss?.();
  };

  return (
    <div
      role="alert"
      aria-live="assertive"
      data-testid="live-broadcast-banner"
      className={`fixed top-0 left-0 right-0 z-[60] px-2 pt-2 transition-transform duration-300 ease-out ${
        enter ? "translate-y-0" : "-translate-y-full"
      }`}
    >
      <div className={`w-full max-w-md mx-auto ${sev.bg} text-white shadow-2xl ring-2 ${sev.ring}`}>
        <button
          type="button"
          onClick={open}
          data-testid="live-broadcast-open"
          className="w-full text-left px-3 py-2 flex items-start gap-2"
        >
          <AlertTriangle className="w-5 h-5 mt-0.5 shrink-0" strokeWidth={2.5} />
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <span className="text-[9px] font-bold tracking-[0.25em] uppercase opacity-90">{sev.label}</span>
              <span className="text-[9px] font-mono opacity-75">il y a {ago(broadcast.created_at)}</span>
            </div>
            <div
              className="font-display text-base font-black leading-tight mt-0.5 truncate"
              data-testid="live-broadcast-title"
            >
              {broadcast.title}
            </div>
            {preview && (
              <div className="text-[12px] leading-tight mt-1 opacity-95 line-clamp-2">{preview}</div>
            )}
            <div className="text-[10px] mt-1 flex items-center gap-1 opacity-90">
              <MapPin className="w-3 h-3" />
              <span className="font-mono tabular-nums">
                {broadcast.latitude?.toFixed(3)}, {broadcast.longitude?.toFixed(3)}
              </span>
              <span>· {broadcast.radius_km} km</span>
            </div>
          </div>
          <ChevronRight className="w-4 h-4 mt-1 shrink-0 opacity-80" />
        </button>
        <button
          type="button"
          data-testid="live-broadcast-dismiss"
          onClick={(e) => { e.stopPropagation(); onDismiss?.(); }}
          aria-label="Fermer"
          className="absolute top-2 right-3 w-6 h-6 grid place-items-center bg-black/30 hover:bg-black/50"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
};
